import { JsonApiError } from '@jaspr/client-js'

/** Every failure the UI shows: a JSON:API error, a JSON-RPC error, a plain error body or a network failure. */
export class ApiError extends Error {
    readonly status: number
    readonly code: string
    readonly title: string
    readonly detail: string
    /** The failing atomic operation's index (`/atomic:operations/N`), or null when the error names none. */
    readonly operation: number | null

    constructor(status: number, code: string, title: string, detail: string, operation: number | null = null) {
        super(detail || title)
        this.name = 'ApiError'
        this.status = status
        this.code = code
        this.title = title
        this.detail = detail
        this.operation = operation
    }
}

interface RpcError {
    code: number
    message: string
    data?: { code?: string | number }
}

function isRpcError(error: unknown): error is RpcError {
    return (
        typeof error === 'object' &&
        error !== null &&
        typeof (error as RpcError).code === 'number' &&
        typeof (error as RpcError).message === 'string'
    )
}

export function isAbort(error: unknown): boolean {
    return typeof error === 'object' && error !== null && (error as { name?: unknown }).name === 'AbortError'
}

function operationOf(pointer: string | undefined): number | null {
    const match = pointer?.match(/^\/atomic:operations\/(\d+)/)
    return match ? Number(match[1]) : null
}

export function toApiError(error: unknown): ApiError {
    if (error instanceof ApiError) {
        return error
    }
    if (error instanceof JsonApiError) {
        const first = error.errors[0]
        const fallback = error.message || `HTTP ${error.status}`
        return new ApiError(
            error.status,
            first?.code ?? String(error.status),
            first?.title || fallback,
            first?.detail || first?.title || fallback,
            operationOf(first?.source?.pointer),
        )
    }
    if (isRpcError(error)) {
        return new ApiError(error.code, String(error.data?.code ?? error.code), error.message, error.message)
    }
    if (error instanceof TypeError) {
        return new ApiError(0, 'network', 'Network error', error.message)
    }
    return new ApiError(0, 'unknown', 'Unexpected error', error instanceof Error ? error.message : String(error))
}

/** Reads a failed response's first JSON:API error (or JSON-RPC error), or falls back to its status. */
export async function apiErrorFromResponse(response: Response): Promise<ApiError> {
    try {
        const body = (await response.json()) as {
            errors?: { code?: string; title?: string; detail?: string }[]
            error?: { message?: string; data?: { code?: string } }
        }
        if (body.error?.message) {
            // JSON-RPC passes its status through, so the failure arrives here, not in the caller's `error` branch.
            return new ApiError(response.status, body.error.data?.code ?? String(response.status), body.error.message, body.error.message)
        }
        const first = body.errors?.[0]
        if (first) {
            return new ApiError(
                response.status,
                first.code ?? String(response.status),
                first.title ?? '',
                first.detail ?? first.title ?? '',
            )
        }
    } catch {
        // not JSON: fall back to the status line
    }
    // HTTP/2 has no reason phrase.
    const text = response.statusText || `HTTP ${response.status}`
    return new ApiError(response.status, String(response.status), text, text)
}
